import React, { useCallback, useState } from "react";

import ArrowRightBigIcon from "images/icons/arrow-right-big-icon.inline.svg";
import ArrowRight from "images/icons/arrow-right.inline.svg";

import { useTheme } from "../../context/ThemeContext";
import { useBannerDetails } from "../../hooks/useBannerManager";
import { useGviconfigurator } from "../../hooks/useSiteConfigurator";
import { Link } from "../../utils/customLink";
import Newsletter from "../newsletter";
import EnquireCard from "../shared/enquireCard";
import CountrySelect from "./countrySelect";

const ContactUsPageComponent = ({ data }) => {
  const configurator = useGviconfigurator();
  const { currentSiteMeta, localeLanguage } = useTheme();

  const siteList = configurator?.world_contact_details ?? [];

  const mainContactDetails = currentSiteMeta?.contact_details ?? [];
  const InfoFC = ({
    title,
    detail,
    btnLabel,
    to = "#",
    target = "",
    className
  }) => {
    return (
      <div className={`flex flex-col justify-between ${className}`}>
        <div>
          <h6 className="uppercase font-bold">{title}</h6>
          <div
            className="text-black-lighter planet:text-gray-generic"
            dangerouslySetInnerHTML={{ __html: detail }}
          />
        </div>
        {btnLabel && (
          <Link
            className="callback-button group inline-flex justify-center items-center w-full md:w-auto py-2 px-5 mt-4 md:mr-auto rounded-full planet:rounded-sm"
            to={to}
            target={target}
          >
            {btnLabel}
            <ArrowRight className="hidden planet:block ml-3 stroke-white planet:group-hover:stroke-gray-darker" />
            <ArrowRightBigIcon className="planet:hidden ml-3 fill-black-lighter group-hover:fill-white" />
          </Link>
        )}
      </div>
    );
  };

  const localeLanguages = Object.keys(siteList);

  const newCountryListArray = localeLanguages.map((rate, idx) => {
    return { label: [rate], value: Object.values(siteList)[idx] };
  });

  const countryList =
    newCountryListArray.map((item, idx) => {
      let countryData = item.value.contact_details ?? {};
      return {
        id: idx,
        value: item?.value?.country ?? "",
        label: item?.value?.country ?? "",
        info: {
          phone:
            `${countryData.calling_from_label} : ${countryData.calling_from_value}<br />International : ${countryData.international}` ??
            "",
          address: countryData?.address?.join("<br />") ?? [],
          google_map_link: countryData?.google_map_link ?? "",
          office_hours: countryData?.office_hours?.join("<br />") ?? [],
          office_closures: countryData?.office_closures?.join("<br />") ?? []
        }
      };
    }) ?? [];

  const {
    title: stickyTitle,
    image: stickyImage,
    desc: stickyDesc
  } = useBannerDetails();

  const enquireCardDetails = {
    cardTitle: null,
    enquireCardImg: stickyImage,
    cardDesc:
      `<div>${stickyTitle || ""}</div><div class='font-normal font-body text-sm pt-4'>${stickyDesc || ""}</div>` ??
      "",
    cardBookDesc: null, //stickyBookDesc ?? "Book by"
    shareButton: false,
    language: localeLanguage
  };

  const [country, setCountry] = useState({});

  const setOpenTab = useCallback((id) => {
    setCountry(countryList[id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="flex justify-center bg-white planet:bg-gray-darker">
        <div
          className={`flex flex-col md:flex-row w-11/12 md:w-5/6 md:px-6 py-14 gap-14 md:gap-36 ${
            country.info ? "md:pb-52" : "md:pb-60"
          }`}
        >
          <div className="w-full">
            <div className="flex flex-col md:flex-row w-full">
              <div className="w-full md:w-5/12 flex flex-col">
                <h2
                  className="font-normal planet:font-semibold font-alice planet:font-body sm:pb-2"
                  dangerouslySetInnerHTML={{ __html: "Office" }}
                />
                <h2
                  className="font-normal planet:font-light font-alice planet:font-body pb-5"
                  dangerouslySetInnerHTML={{ __html: "contact details" }}
                />
                <InfoFC
                  title="phone"
                  detail={` ${mainContactDetails.calling_from_label} : ${mainContactDetails.calling_from_value}<br />International : ${mainContactDetails.international}`}
                  btnLabel="Call back request"
                  to="/enquiry-form/?type=callback"
                  className="mt-auto"
                />
              </div>
              <div className="w-full md:w-7/12 pt-8">
                <div className={"flex justify-between w-full"}>
                  <InfoFC
                    title="Open during"
                    detail={mainContactDetails?.office_hours?.join("<br />")}
                    className="pb-4"
                  />
                  {mainContactDetails?.office_closures?.length > 0 && (
                    <InfoFC
                      title="Office closures"
                      detail={mainContactDetails?.office_closures?.join(
                        "<br />"
                      )}
                      className="pb-4"
                    />
                  )}
                </div>
                <InfoFC
                  title="address"
                  detail={mainContactDetails?.address?.join("<br />")}
                  btnLabel="View on Google Maps"
                  to={mainContactDetails.google_map_link}
                  target={"_gviblank"}
                />
              </div>
            </div>
            <div className="pt-14 md:pt-20">
              <h2 className="font-normal planet:font-semibold font-alice planet:font-body pb-9">
                Worldwide contact details
              </h2>
              <CountrySelect
                options={countryList}
                className="w-full md:w-2/5 h7 uppercase z-20"
                setOpenTab={setOpenTab}
              />
              {country.info && (
                <>
                  <h2 className="font-normal planet:font-light font-alice planet:font-body pt-12 pb-8">
                    {country.label}
                  </h2>
                  <div className="flex flex-col md:flex-row w-full gap-5 md:gap-0">
                    <div className="w-full md:w-7/12 flex flex-col md:flex-row justify-between gap-8 md:gap-0">
                      <InfoFC
                        title="phone"
                        detail={country.info.phone}
                        btnLabel="Call back request"
                        to="/enquiry-form/?type=callback"
                      />
                    </div>
                    <div>
                      <InfoFC
                        title="address"
                        detail={country.info.address}
                        btnLabel="View on Google Maps"
                        to={country.info.google_map_link}
                        target={"_gviblank"}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="hidden md:block">
            <EnquireCard
              applyNow={"/application-form/"}
              enquireNow={"/enquiry-form/"}
              enquireCardDetails={enquireCardDetails}
            />
          </div>
        </div>
      </div>

      <Newsletter />
    </>
  );
};

export default ContactUsPageComponent;
